import {
  DestroyRef,
  Directive,
  ElementRef,
  InjectionToken,
  NgZone,
  PLATFORM_ID,
  afterNextRender,
  inject,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

/**
 * CSS custom property (on `:root`) holding the viewport y of the app header's
 * bottom edge, in px. Consumers use it with a fallback, e.g.
 * `top: var(--rz-header-bottom, 3rem)`.
 */
export const HEADER_BOTTOM_CSS_VAR = '--rz-header-bottom';

/**
 * The instance that currently owns {@link HEADER_BOTTOM_CSS_VAR}. The property
 * is document-global, so only the most recently started instance writes it and
 * only that instance removes it — a stale instance torn down late (e.g. across
 * an auth/app layout switch) cannot wipe the value the live navbar published.
 */
let activeOwner: object | null = null;

/** The slice of `ResizeObserver` this directive uses. */
export interface HeaderResizeObserver {
  observe(target: Element): void;
  disconnect(): void;
}

/**
 * Creates the observer that re-measures the header. Returns `null` when the
 * platform has no `ResizeObserver` (the directive then falls back to `resize`).
 */
export type HeaderResizeObserverFactory = (onResize: () => void) => HeaderResizeObserver | null;

/**
 * Injection seam for {@link HeaderResizeObserverFactory}. Production uses the
 * browser's `ResizeObserver`; tests provide a fake so re-measuring is driven
 * deterministically instead of waiting on the browser's own observer timing.
 */
export const HEADER_RESIZE_OBSERVER_FACTORY = new InjectionToken<HeaderResizeObserverFactory>(
  'HEADER_RESIZE_OBSERVER_FACTORY',
  {
    providedIn: 'root',
    factory: () => {
      const view = inject(DOCUMENT).defaultView;
      return (onResize: () => void): HeaderResizeObserver | null =>
        view && typeof view.ResizeObserver === 'function'
          ? new view.ResizeObserver(() => onResize())
          : null;
    },
  },
);

/**
 * F-FU-45 · Publishes the REAL bottom edge of the element it sits on (the app
 * navbar) as {@link HEADER_BOTTOM_CSS_VAR} so fixed overlays anchored "below the
 * header" (top toasts) follow it.
 *
 * Why measure instead of deriving it from banner state: the service-worker
 * update banner and the mock-data banner render ABOVE the navbar, and the update
 * banner wraps to several lines on narrow screens — its height is layout, not
 * state. A ResizeObserver watches the header AND its parent column: a banner
 * appearing, wrapping or disappearing changes the column's height (the shell is
 * `h-screen`), which is exactly when the header's bottom edge moves.
 *
 * The observer runs outside Angular (it only writes a style property) and is
 * disconnected on destroy, when the property is also removed so consumers fall
 * back to their default (e.g. on /auth pages that have no navbar).
 *
 * SINGLE-USE: put it on the one app navbar (app.component.html). The variable
 * is global, so a second live instance would fight over it; ownership is
 * last-started-wins and a non-owner never writes or removes it.
 */
@Directive({
  selector: '[appPublishHeaderBottom]',
  standalone: true,
})
export class PublishHeaderBottomDirective {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly createObserver = inject(HEADER_RESIZE_OBSERVER_FACTORY);

  private observer: HeaderResizeObserver | null = null;
  private removeResizeListener: (() => void) | null = null;

  constructor() {
    if (!this.isBrowser) {
      return;
    }
    afterNextRender(() => this.zone.runOutsideAngular(() => this.start()));
    this.destroyRef.onDestroy(() => this.stop());
  }

  /** Measure and publish the header's bottom edge now (owner only). */
  publish(): void {
    if (activeOwner !== this) {
      return;
    }
    const bottom = Math.max(0, Math.round(this.host.nativeElement.getBoundingClientRect().bottom));
    this.document.documentElement.style.setProperty(HEADER_BOTTOM_CSS_VAR, `${bottom}px`);
  }

  private start(): void {
    activeOwner = this;
    this.publish();
    const view = this.document.defaultView;
    const element = this.host.nativeElement;
    this.observer = this.createObserver(() => this.publish());
    if (this.observer) {
      this.observer.observe(element);
      if (element.parentElement) {
        this.observer.observe(element.parentElement);
      }
      return;
    }
    // No ResizeObserver: a viewport resize is the only remaining signal.
    if (view) {
      const onResize = (): void => this.publish();
      view.addEventListener('resize', onResize, { passive: true });
      this.removeResizeListener = () => view.removeEventListener('resize', onResize);
    }
  }

  private stop(): void {
    this.observer?.disconnect();
    this.observer = null;
    this.removeResizeListener?.();
    this.removeResizeListener = null;
    if (activeOwner === this) {
      activeOwner = null;
      this.document.documentElement.style.removeProperty(HEADER_BOTTOM_CSS_VAR);
    }
  }
}
